// RatioChart.js
import React, { useState, useMemo } from "react";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from "recharts";
import "./RatioChart.css";
import { METRIC_FIELDS as METRIC_OPTIONS } from "../../constants/metricFields";


// const METRIC_OPTIONS = [
//   { value: "currentRatio", label: "Current Ratio" },
//   { value: "quickRatio", label: "Quick Ratio" },
//   { value: "grossProfitMargin", label: "Gross Profit Margin" },
//   { value: "operatingProfitMargin", label: "Operating Profit Margin" },
//   { value: "netProfitMargin", label: "Net Profit Margin" },
//   { value: "returnOnAssets", label: "Return on Assets" },
//   // { value: "returnOnEquity", label: "Return on Equity" },
//   { value: "debtToEquityRatio", label: "Debt to Equity Ratio" },
//   { value: "freeCashFlowPerShare", label: "Free Cash Flow per Share" },
//   { value: "freeCashFlowOperatingCashFlowRatio", label: "FCF / Operating CF Ratio" },
//   // { value: "priceToFreeCashFlowsRatio", label: "Price to Free Cash Flow Ratio" },
//   { value: "priceToEarningsRatio", label: "Price to Earnings Ratio" },
//   { value: "priceToSalesRatio", label: "Price to Sales Ratio" }
// ];

const RatioChart = ({ chartData = [], stockInfo, dataKey, label }) => {
  // Hooks must run before any early return
  const [metricKey, setMetricKey] = useState(dataKey);

  const currentLabel = useMemo(
    () => METRIC_OPTIONS.find(m => m.value === metricKey)?.label || label,
    [metricKey, label]
  );

  const formattedData = useMemo(() => {
    const src = Array.isArray(chartData) ? chartData : [];
    return src
      .map(d => ({
        formattedDate: Number(String(d.date).slice(0, 4)),
        // value: Number(d[metricKey]),
        value: d[metricKey] !== undefined && d[metricKey] !== null
          ? Number(d[metricKey])
          : null,
      }))
      // *** Be careful with this - maybe revert back to the line below, not sure this works ***
      // .filter(d => Number.isFinite(d.value))
      // .filter(d => d.value !== undefined && d.value !== null && !isNaN(d.value))
      .filter(d => d.value !== null)

  }, [chartData, metricKey]);

  const hasData = formattedData.length > 0;

  const values = hasData ? formattedData.map(d => d.value) : [0];
  const minVal = Math.min(...values) >= 0 ? 0 : Math.min(...values);
  const maxVal = Math.max(...values);

  // Calculate axis scale using strict multiples of 5 or 10
  const calculateAxisScale = (min, max, desired = 6) => {
    let span = max - min;
    if (span === 0) {
      const step = Math.max(1, Math.pow(10, Math.floor(Math.log10(Math.abs(min) || 1)))) / 10;
      const scaleMin = min - step;
      const scaleMax = min + step;
      const decimals = Math.max(0, -Math.floor(Math.log10(step)));
      return { step, scaleMin, scaleMax, decimals };
    }

    const rawStep = span / Math.max(1, desired - 1);
    let magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    let normalized = rawStep / magnitude;

    const chosen = normalized <= 5 ? 5 : 10;

    const step = chosen * magnitude;
    const scaleMin = Math.floor(min / step) * step;
    const scaleMax = Math.ceil(max / step) * step;
    const decimals = Math.max(0, -Math.floor(Math.log10(step)));
    return { step, scaleMin, scaleMax, decimals };
  };

  const { step, scaleMin, scaleMax, decimals } = calculateAxisScale(minVal, maxVal, 6);
  const ticks = Array.from(
    { length: Math.round((scaleMax - scaleMin) / step) + 1 },
    (_, i) => Number((scaleMin + i * step).toFixed(decimals))
  );

  const formatTick = (v) => {
    const d = step < 1 || Math.abs(v) < 1 ? decimals : 0;
    return Number(Number(v).toFixed(d));
  };

  // if (!hasData) return <p>No data to display.</p>;
  const safeData = hasData ? formattedData : [];

  return (
    
    <div className="chart-content">

      <h3 className="chart-title">
        {stockInfo?.name}: {currentLabel}
      </h3>

      <div className="chart-container ratio-chart-container" >
        
        {/* Per-chart metric selector (top-right, above chart face) */}
        <div
          className="metric-selector"
          
        >
          <label htmlFor={`ratio-metric-${stockInfo?.ticker}`}>Metric selector:&nbsp;</label>
          <select
            id={`ratio-metric-${stockInfo?.ticker}`}
            value={metricKey}
            onChange={(e) => setMetricKey(e.target.value)}
          >
            {METRIC_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="ratio-chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLineChart
              // data={formattedData}
              data={safeData}
              margin={{ top: 10, right: 10, left: 10, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="formattedDate"
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
                label={{ value: "Year", position: "bottom" }}
              />
              <YAxis
                domain={[scaleMin, scaleMax]}
                tick={{ fontSize: 12 }}
                ticks={ticks}
                allowDecimals
                tickFormatter={formatTick}
                label={{ value: currentLabel, angle: -90, position: "insideLeft", dy: 70 }}
              />
              <Tooltip
                formatter={(v) => {
                  const d = step < 1 || Math.abs(v) < 1 ? decimals : 0;
                  return [Number(v).toFixed(d), currentLabel];
                }}
                labelFormatter={(l) => `Year: ${l}`}
              />
              <Legend verticalAlign="top" align="center" wrapperStyle={{ paddingTop: 10 }} />
              <Line
                type="monotone"
                dataKey="value"
                name={currentLabel}
                stroke="#82ca9d"
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 5 }}
              />
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default RatioChart;
